import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { ChevronLeft, Trophy, CheckCircle, XCircle } from "lucide-react"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Text } from "@/components/ui/text"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { SafeHtml } from "@/components/ui/safe-html"

export const Route = createFileRoute("/(app)/app/stats/$quizId")({
  component: RouteComponent,
})

function RouteComponent() {
  const { quizId } = Route.useParams()

  const results = useQuery(api.quizzes.handlers.getQuizResultsForUser, {
    quizId: quizId as Id<"quizzes">,
  })

  if (results === undefined) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[200px] w-full" />
      </div>
    )
  }

  if (results === null) {
    return (
      <div className="p-4">
        <h1 className="text-3xl font-bold">Quiz not found</h1>
        <p className="text-muted-foreground">
          The quiz you're looking for doesn't exist.
        </p>
      </div>
    )
  }

  const { quiz, userResult, leaderboardPosition } = results

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/app" search={{ timeFilter: "past" }}>
            <ChevronLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">{quiz.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary">{quiz.type}</Badge>
            {quiz.resultsPublished ? (
              <Badge className="bg-green-500">Results Published</Badge>
            ) : (
              <Badge variant="outline">Results Pending</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Results not published message */}
      {!quiz.resultsPublished && (
        <Card>
          <CardContent className="py-8 text-center">
            <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <Text className="text-lg font-medium">
              Results Not Yet Published
            </Text>
            <Text className="text-muted-foreground">
              The administrator has not yet published results for this quiz.
              Check back later!
            </Text>
          </CardContent>
        </Card>
      )}

      {/* User Performance Card */}
      {userResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Your Performance</span>
              {leaderboardPosition && (
                <Badge
                  variant={
                    leaderboardPosition.rank <= 3 ? "default" : "outline"
                  }
                  className={
                    leaderboardPosition.rank === 1
                      ? "bg-yellow-500 text-lg px-4 py-1"
                      : leaderboardPosition.rank === 2
                        ? "bg-gray-400 text-lg px-4 py-1"
                        : leaderboardPosition.rank === 3
                          ? "bg-amber-600 text-lg px-4 py-1"
                          : "text-lg px-4 py-1"
                  }
                >
                  Rank #{leaderboardPosition.rank} of{" "}
                  {leaderboardPosition.total}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Text className="text-3xl font-bold">
                  {userResult.totalScore.toFixed(
                    quiz.type === "daily" ? 2 : 0
                  )}
                </Text>
                <Text className="text-sm text-muted-foreground">
                  Total Score
                </Text>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Text className="text-3xl font-bold">
                  {userResult.answers.filter((a) => a.isCorrect).length}
                </Text>
                <Text className="text-sm text-muted-foreground">Correct</Text>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Text className="text-3xl font-bold">
                  {userResult.answers.length}
                </Text>
                <Text className="text-sm text-muted-foreground">
                  Total Questions
                </Text>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <Text className="text-3xl font-bold">
                  {Math.round(
                    (userResult.answers.filter((a) => a.isCorrect).length /
                      userResult.answers.length) *
                      100
                  )}
                  %
                </Text>
                <Text className="text-sm text-muted-foreground">Accuracy</Text>
              </div>
            </div>

            {/* Question breakdown - only show if results are published */}
            {quiz.resultsPublished && (
              <div>
                <h3 className="font-semibold mb-4">Question Breakdown</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Question</TableHead>
                      <TableHead>Your Answer</TableHead>
                      <TableHead>Correct Answer</TableHead>
                      <TableHead className="text-right">Points</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userResult.answers.map((answer, index) => (
                      <TableRow key={answer.questionId}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell className="max-w-xs truncate">
                          {"questionText" in answer ? (
                            <SafeHtml html={answer.questionText ?? ""} />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {answer.isCorrect ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            <span
                              className={
                                answer.isCorrect
                                  ? "text-green-600"
                                  : "text-red-600"
                              }
                            >
                              {answer.userAnswer || "(no answer)"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {"correctAnswer" in answer ? (
                            <SafeHtml html={answer.correctAnswer ?? ""} />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {"points" in answer ? (
                            <Badge variant="secondary">
                              {typeof answer.points === "number"
                                ? answer.points.toFixed(2)
                                : "0"}
                            </Badge>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
